// The pilot zones on the real map, for the Marketeurs tab (design « Les
// quartiers », added by Ruben on 2 Nov 2026): each zone's outline, and at its
// centre a label with the brand that leads there. Same base map and zone marks
// as the live map; scrolling the page never zooms it, the buttons do.
import { useEffect, useRef, useState } from 'react';
import maplibregl, { type GeoJSONSource, type Map as MlMap, type Marker } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { zoneShapes } from '../lib/zones';
import type { Zone } from '../lib/brands';
import { S } from '../lib/strings';

const STYLE = 'https://tiles.openfreemap.org/styles/positron';
const ZONE_FILL = 'rgba(14,116,188,0.09)';
const ZONE_LINE = 'rgba(14,116,188,0.6)';

export interface ZoneBrand { name: string; logo?: string | null }

// Built with DOM calls, not innerHTML: names come from the database.
function label(z: Zone, b: ZoneBrand | undefined, onClick: () => void) {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'zm-l';
  const tile = document.createElement('span');
  tile.className = 'zm-logo';
  if (b?.logo) {
    const img = document.createElement('img');
    img.src = b.logo;
    img.alt = b.name;
    img.onerror = () => { img.remove(); tile.textContent = b.name; tile.classList.add('tx'); };
    tile.append(img);
  } else {
    tile.textContent = b ? b.name : S.mk.tie(z.counts.length);
    tile.classList.add('tx');
  }
  const t = document.createElement('span');
  t.className = 'zm-t';
  const q = document.createElement('b');
  q.textContent = z.name;
  const h = document.createElement('em');
  h.textContent = S.mk.homes(z.n);
  t.append(q, h);
  el.append(tile, t);
  el.onclick = onClick;
  return el;
}

/** The zones drawn flat, when the browser cannot run the map (no WebGL). */
function Flat({ zones, brands, onZone }: { zones: Zone[]; brands: Record<string, ZoneBrand>; onZone?: (name: string) => void }) {
  const shapes = zoneShapes(zones.map((z) => z.name)).features;
  const pts = shapes.flatMap((f) => f.geometry.coordinates[0]);
  if (!pts.length) return <div className="zm-flat"><p>{S.mk.noMap}</p></div>;
  const [x0, x1, y0, y1] = [Math.min(...pts.map((p) => p[0])), Math.max(...pts.map((p) => p[0])), Math.min(...pts.map((p) => p[1])), Math.max(...pts.map((p) => p[1]))];
  const W = 640, H = 520, pad = 70, k = Math.min((W - 2 * pad) / (x1 - x0 || 1), (H - 2 * pad) / (y1 - y0 || 1));
  const X = (lng: number) => pad + (lng - x0) * k + (W - 2 * pad - (x1 - x0) * k) / 2;
  const Y = (lat: number) => H - pad - (lat - y0) * k - (H - 2 * pad - (y1 - y0) * k) / 2;
  return (
    <div className="zm-flat">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={S.mk.zones}>
        {shapes.map((f) => {
          const ring = f.geometry.coordinates[0];
          const z = zones.find((x) => x.name === f.properties.name);
          const cx = ring.reduce((a, p) => a + X(p[0]), 0) / ring.length, cy = ring.reduce((a, p) => a + Y(p[1]), 0) / ring.length;
          const b = z?.leader ? brands[z.leader] : undefined;
          return (
            <g key={f.properties.name} onClick={() => onZone?.(f.properties.name)}>
              <polygon points={ring.map((p) => `${X(p[0])},${Y(p[1])}`).join(' ')} />
              <text x={cx} y={cy - 4} textAnchor="middle" className="q">{f.properties.name}</text>
              {z && <text x={cx} y={cy + 14} textAnchor="middle" className="l">{b ? `${b.name} · ${S.mk.homes(z.n)}` : S.mk.tie(z.counts.length)}</text>}
            </g>
          );
        })}
      </svg>
      <p>{S.mk.noMap}</p>
    </div>
  );
}

export function ZoneMap({ zones, brands, onZone }: { zones: Zone[]; brands: Record<string, ZoneBrand>; onZone?: (name: string) => void }) {
  const [noGl, setNoGl] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<MlMap | null>(null);
  const marks = useRef<Marker[]>([]);
  const ready = useRef(false);
  const draw = useRef<() => void>(() => {});

  draw.current = () => {
    const m = map.current;
    if (!m || !ready.current) return;
    const shapes = zoneShapes(zones.map((z) => z.name));
    (m.getSource('zones') as GeoJSONSource | undefined)?.setData(shapes);
    marks.current.forEach((k) => k.remove());
    marks.current = [];
    const pts: [number, number][] = [];
    shapes.features.forEach((f) => {
      const ring = f.geometry.coordinates[0];
      ring.forEach((p) => pts.push(p));
      const c = ring.reduce<[number, number]>((a, p) => [a[0] + p[0] / ring.length, a[1] + p[1] / ring.length], [0, 0]);
      const z = zones.find((x) => x.name === f.properties.name);
      if (!z) return;
      const el = label(z, z.leader ? brands[z.leader] : undefined, () => onZone?.(z.name));
      marks.current.push(new maplibregl.Marker({ element: el }).setLngLat(c).addTo(m));
    });
    if (pts.length > 1) m.fitBounds(pts.reduce((bb, p) => bb.extend(p), new maplibregl.LngLatBounds(pts[0], pts[0])), { padding: { top: 70, bottom: 50, left: 90, right: 90 }, maxZoom: 14, duration: 0 });
  };

  useEffect(() => {
    if (!box.current) return;
    let m: MlMap;
    try {
      m = new maplibregl.Map({ container: box.current, style: STYLE, center: [11.51, 3.87], zoom: 12, attributionControl: false, dragRotate: false, pitchWithRotate: false, scrollZoom: false });
    } catch {
      setNoGl(true); // no WebGL on this computer: the zones are drawn flat instead
      return;
    }
    m.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-left');
    m.on('load', () => {
      const firstSymbol = m.getStyle().layers.find((l) => l.type === 'symbol')?.id;
      m.addSource('zones', { type: 'geojson', data: zoneShapes([]) });
      m.addLayer({ id: 'zone-fill', type: 'fill', source: 'zones', paint: { 'fill-color': ZONE_FILL } }, firstSymbol);
      m.addLayer({ id: 'zone-line', type: 'line', source: 'zones', paint: { 'line-color': ZONE_LINE, 'line-width': 1.6, 'line-dasharray': [3, 2.5] } }, firstSymbol);
      ready.current = true;
      draw.current();
    });
    map.current = m;
    // A tab kept in the background is display:none; the map redraws at full size when it shows again.
    const ro = new ResizeObserver(() => { if (box.current?.clientWidth) m.resize(); });
    ro.observe(box.current);
    return () => { ro.disconnect(); marks.current.forEach((k) => k.remove()); m.remove(); map.current = null; ready.current = false; };
  }, []);

  useEffect(() => { draw.current(); }, [zones, brands]);

  if (noGl) return <Flat zones={zones} brands={brands} onZone={onZone} />;
  return (
    <div className="map lmap zmap">
      <div ref={box} className="lmap-gl" />
      <span className="zm-key"><span className="d" />{S.zones} · {S.mk.city}</span>
      <div className="mctl">
        <button aria-label="Zoom +" onClick={() => map.current?.zoomIn()}><svg className="i s" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg></button>
        <button aria-label="Zoom −" onClick={() => map.current?.zoomOut()}><svg className="i s" viewBox="0 0 24 24"><path d="M5 12h14" /></svg></button>
      </div>
    </div>
  );
}
